/**
 * Analysis run JSON DTO. Business descriptions copied from the backend AiAnalysisRun model.
 * Date attributes cross the API as UTC strings.
 * @typedef {Object} AiAnalysisRunDto
 * @property {number} id Unique identifier of this analysis run.
 * @property {number} offer_id ID of the offer being analyzed.
 * @property {'launch' | 'cr' | 'assessment'} kind Row role: launch owns every analyzer result, cr is a legacy pre-kind row that is also its own result, assessment is the analyzer child. A historical pre-kind row reads as cr.
 * @property {number | null} parent_run_id Launch a row belongs to; null for a launch and for a historical pre-kind run.
 * @property {'user' | 'system'} trigger Run origin: user for a user-initiated request, system for an automated request.
 * @property {string | null} requested_by Authenticated user ID stored as text for user runs; null for system runs.
 * @property {'queued' | 'running' | 'completed' | 'failed'} status Execution state: queued, running, completed, or failed; failed runs are not retried automatically.
 * @property {string} period_start First analyzed calendar day in UTC, inclusive.
 * @property {string} period_end Last analyzed calendar day in UTC, inclusive.
 * @property {string | null} model OpenRouter model ID selected when execution starts; null before selection.
 * @property {string | null} summary_points JSON array string: the CR analyzer sends 1-8 {category: 'positive'|'risk'|'warning'|'opportunity', text} points and the assessment sends 2-5 of the same categorized shape, while an assessment row written before that shape still holds 2-5 plain strings whose order is their business importance; null until analysis completes successfully. The frontend parses both for display.
 * @property {string | null} cost_usd Known OpenRouter spend in USD across all requests, including failed runs; null before accounting, may be partial when is_cost_complete is false.
 * @property {boolean} is_cost_complete Whether cost_usd includes reported costs for every attempted model request; false when any request cost is unknown.
 * @property {Record<string, unknown>} meta Compact analysis totals: input/output/total tokens, model/tool call counts, and final caveat; no request history or MCP responses.
 * @property {string | null} error Failure description safe for display; null when no failure has been recorded.
 * @property {string} created_at Queue admission timestamp in UTC; used for the automatic admission cooldown.
 * @property {string | null} started_at Timestamp in UTC when the worker claimed the run; null while queued.
 * @property {string | null} finished_at Timestamp in UTC when the run completed or failed; null before termination.
 * @property {string | null} [health] Planned backend assessment display value; semantics and scale require backend design. Never inferred from summary.
 * @property {string | null} [risk_level] Planned backend assessment display value; semantics and scale require backend design. Never inferred from summary.
 * @property {string | null} [trend] Backend assessment trend: 'declining' | 'improving' | 'stable' | 'mixed' | null. Null means the evidence does not support it and is rendered as an em dash.
 * @property {string | null} [confidence] Planned backend assessment display value; semantics and scale require backend design. Never inferred from summary.
 * @property {Array<Record<string, unknown>> | null} [analyses] Analyzer children, present only on the single-run read and absent on a legacy pre-kind row. The list endpoint never nests them.
 */

/**
 * Analyzer child JSON DTO, an entry of the additive `analyses` array on the single-run read
 * (`GET /:offerId/analyses/:runId`). Carries the assessment verdict and its own cost columns.
 * @typedef {Object} AiAnalysisChildDto
 * @property {number} id Unique identifier of this analyzer child row.
 * @property {string} kind Analyzer that produced the row, for example 'cr' or 'assessment'; the assessment is the analyzer carrying the verdict.
 * @property {'queued' | 'running' | 'completed' | 'failed'} status Execution state of this analyzer.
 * @property {string | null} health Assessment verdict: 'healthy' | 'attention' | 'critical' | null.
 * @property {string | null} risk_level Assessment verdict: 'low' | 'medium' | 'high' | null.
 * @property {'declining' | 'improving' | 'stable' | 'mixed' | null} trend Assessment verdict: direction of the measured CR movement. mixed means movement in both directions at once.
 * @property {string | null} confidence Assessment verdict: 'low' | 'medium' | 'high' | null.
 * @property {string | null} summary_points JSON array string: the CR child sends 1-8 {category, text} points and the assessment child sends 2-5 of the same categorized shape, while an assessment child written before that shape still holds 2-5 plain strings ordered by business importance; the frontend parses both.
 * @property {string | null} caveat Flat statement lifted out of the child meta; null when the model gave none. The raw meta is not part of analyses.
 * @property {{ health: string | null, risk_level: string | null, trend: string | null, confidence: string | null, probabilities: Record<string, Record<string, number>> } | null} [indicators_jev] Assessment child only: the decision model's (Jev) reading of the same four indicators with its probability per option, or null when the run stored none. The columns keep the model's verdict; this is a second opinion the panel shows on request.
 * @property {Array<{ code: string, path: string, message: string }>} warnings Output checks that fired on the completed result, always present and empty on a clean child. An output check no longer fails the run: the run completes and the check is published here, with `message` a full sentence quoting the offending summary point or finding, `code` a stable rule identifier and `path` the location in the model output. The panel lists the messages above the summary points.
 * @property {string | null} cost_usd This child's own known spend as a decimal string.
 * @property {boolean} is_cost_complete Whether this child's cost_usd covers every attempted request.
 * @property {string | null} error Analyzer failure text on a failed child; the launch error already summarizes failed children.
 * @property {string} created_at Timestamp in UTC when this child row was created.
 * @property {string | null} started_at Timestamp in UTC when the child started; null while queued.
 * @property {string | null} finished_at Timestamp in UTC when the child terminated; null before termination.
 */

/**
 * Analysis run domain entity built from the API DTO.
 * Field descriptions duplicate the AiAnalysisRunDto typedef above; keep both in sync.
 */
export class AiAnalysisRun {
  /** @param {AiAnalysisRunDto} data */
  constructor(data) {
    /** @type {number} Unique identifier of this analysis run. */
    this.id = data.id;
    /** @type {number} ID of the offer being analyzed. */
    this.offer_id = data.offer_id;
    /** @type {'launch' | 'cr' | 'assessment' | null} Row role; null only when the API did not send kind. */
    this.kind = data.kind ?? null;
    /** @type {number | null} Launch this row belongs to; null for a launch and for a legacy pre-kind run. */
    this.parent_run_id = data.parent_run_id ?? null;
    /** @type {'user' | 'system'} Run origin: user for a user-initiated request, system for an automated request. */
    this.trigger = data.trigger;
    /** @type {string | null} Authenticated user ID stored as text for user runs; null for system runs. */
    this.requested_by = data.requested_by;
    /** @type {'queued' | 'running' | 'completed' | 'failed'} Execution state: queued, running, completed, or failed; failed runs are not retried automatically. */
    this.status = data.status;
    /** @type {string} First analyzed calendar day in UTC, inclusive. */
    this.period_start = data.period_start;
    /** @type {string} Last analyzed calendar day in UTC, inclusive. */
    this.period_end = data.period_end;
    /** @type {string | null} OpenRouter model ID selected when execution starts; null before selection. */
    this.model = data.model;
    /** @type {string | null} JSON array string: the CR analyzer sends 1-8 {category: 'positive'|'risk'|'warning'|'opportunity', text} points and the assessment sends 2-5 of the same categorized shape, while an assessment row written before that shape still holds 2-5 plain strings whose order is their business importance; null until analysis completes successfully. The frontend parses both for display. */
    this.summary_points = data.summary_points ?? null;
    /** @type {string | null} Known OpenRouter spend in USD across all requests, including failed runs; null before accounting, may be partial when is_cost_complete is false. */
    this.cost_usd = data.cost_usd;
    /** @type {boolean} Whether cost_usd includes reported costs for every attempted model request; false when any request cost is unknown. */
    this.is_cost_complete = data.is_cost_complete;
    /** @type {Record<string, unknown>} Compact analysis totals: input/output/total tokens, model/tool call counts, and final caveat; no request history or MCP responses. */
    this.meta = data.meta;
    /** @type {string | null} Failure description safe for display; null when no failure has been recorded. */
    this.error = data.error;
    /** @type {string} Queue admission timestamp in UTC; used for the automatic admission cooldown. */
    this.created_at = data.created_at;
    /** @type {string | null} Timestamp in UTC when the worker claimed the run; null while queued. */
    this.started_at = data.started_at;
    /** @type {string | null} Timestamp in UTC when the run completed or failed; null before termination. */
    this.finished_at = data.finished_at;
    /** @type {string | null} Planned backend assessment display value; semantics and scale require backend design. Never inferred from summary. */
    this.health = data.health ?? null;
    /** @type {string | null} Planned backend assessment display value; semantics and scale require backend design. Never inferred from summary. */
    this.risk_level = data.risk_level ?? null;
    /** @type {string | null} Backend assessment trend: 'declining' | 'improving' | 'stable' | 'mixed' | null. Null means the evidence does not support it and is rendered as an em dash. */
    this.trend = data.trend ?? null;
    /** @type {string | null} Planned backend assessment display value; semantics and scale require backend design. Never inferred from summary. */
    this.confidence = data.confidence ?? null;
    /** @type {Array<Record<string, unknown>> | null} Analyzer children from the single-run read; null when the read carried no analyses, including a legacy pre-kind row. */
    this.analyses = data.analyses ?? null;
    /** @type {AiAnalysisAssessment | null} Assessment analyzer child of this launch; null on a legacy pre-kind row, which is its own result. */
    this.assessment = AiAnalysisRun.assessmentOf(this.analyses);
  }

  /**
   * First child of kind 'assessment', mapped to the verdict entity; null when the read carried
   * no analyses or none of them is the assessment. Children stay in the order the API sent them.
   * @param {Array<Record<string, unknown>> | null | undefined} analyses
   * @returns {AiAnalysisAssessment | null}
   */
  static assessmentOf(analyses) {
    if (!Array.isArray(analyses)) return null;
    const child = analyses.find((entry) => entry?.kind === 'assessment');
    return child ? new AiAnalysisAssessment(child) : null;
  }
}

/**
 * Verdict-carrying analyzer child of a launch. The launch row itself writes no verdict, so the
 * panel reads health, risk_level, trend, confidence, summary_points and caveat from here.
 * Field descriptions duplicate the AiAnalysisChildDto typedef above; keep both in sync.
 */
export class AiAnalysisAssessment {
  /** @param {AiAnalysisChildDto} data */
  constructor(data) {
    /** @type {number} Unique identifier of this analyzer child row. */
    this.id = data.id;
    /** @type {string} Analyzer that produced the row. */
    this.kind = data.kind;
    /** @type {'queued' | 'running' | 'completed' | 'failed'} Execution state of this analyzer. */
    this.status = data.status;
    /** @type {string | null} Assessment verdict: 'healthy' | 'attention' | 'critical' | null. */
    this.health = data.health ?? null;
    /** @type {string | null} Assessment verdict: 'low' | 'medium' | 'high' | null. */
    this.risk_level = data.risk_level ?? null;
    /** @type {string | null} Assessment verdict: 'declining' | 'improving' | 'stable' | 'mixed' | null. */
    this.trend = data.trend ?? null;
    /** @type {string | null} Assessment verdict: 'low' | 'medium' | 'high' | null. */
    this.confidence = data.confidence ?? null;
    /** @type {string | null} JSON array string: the CR child sends 1-8 {category, text} points and the assessment child sends 2-5 of the same categorized shape, while an assessment child written before that shape still holds 2-5 plain strings ordered by business importance; the frontend parses both. */
    this.summary_points = data.summary_points ?? null;
    /** @type {string | null} Flat statement lifted out of the child meta; null when the model gave none. */
    this.caveat = data.caveat ?? null;
    /** @type {Array<{ code: string, path: string, message: string }>} Output checks that fired on the completed result; empty on a clean child and on a row written before the field existed. */
    this.warnings = analysisWarnings(data);
    /** @type {JevIndicators | null} Jev's reading of the four indicators with its probabilities; null when the run stored none or the payload is not the documented shape. */
    this.indicators_jev = analysisJevIndicators(data.indicators_jev);
    /** @type {string | null} This child's own known spend as a decimal string. */
    this.cost_usd = data.cost_usd ?? null;
    /** @type {boolean} Whether this child's cost_usd covers every attempted request. */
    this.is_cost_complete = data.is_cost_complete;
    /** @type {string | null} Analyzer failure text on a failed child. */
    this.error = data.error ?? null;
    /** @type {string} Timestamp in UTC when this child row was created. */
    this.created_at = data.created_at;
    /** @type {string | null} Timestamp in UTC when the child started; null while queued. */
    this.started_at = data.started_at ?? null;
    /** @type {string | null} Timestamp in UTC when the child terminated; null before termination. */
    this.finished_at = data.finished_at ?? null;
  }
}

/**
 * Stat-box tone of an assessment indicator; gray is the neutral fallback and the
 * empty state (value absent or outside the backend enum).
 * @typedef {'green' | 'amber' | 'red' | 'gray'} IndicatorTone
 */

/**
 * Marker rendered before the stat-box value: a colored dot for state words, a colored
 * direction glyph for the trend, a plain colored value for confidence. null is the
 * empty state and renders the em dash alone.
 * @typedef {'dot' | 'arrow' | 'text'} IndicatorMarker
 */

/**
 * Verdict indicator mapper for the assessment stat boxes, mirroring the enum vocabularies
 * documented in ../../octava-api/docs/offer-ai-api-contract.md. Values are read verbatim: an
 * unknown or missing value is never defaulted, it renders as the em dash empty state.
 * Trend owns the arrow marker and the four-value enum including 'mixed'.
 */
const ANALYSIS_INDICATORS = [
  {
    key: 'health',
    label: 'Health',
    tones: { healthy: 'green', attention: 'amber', critical: 'red' },
    labels: { healthy: 'Good', attention: 'Attention', critical: 'Critical' },
    marker: 'dot',
  },
  {
    key: 'risk_level',
    label: 'Risk level',
    tones: { low: 'green', medium: 'amber', high: 'red' },
    labels: { low: 'Low', medium: 'Medium', high: 'High' },
    marker: 'dot',
  },
  {
    key: 'trend',
    label: 'Trend',
    tones: { declining: 'red', improving: 'green', stable: 'gray', mixed: 'amber' },
    labels: { declining: 'Declining', improving: 'Improving', stable: 'Stable', mixed: 'Mixed' },
    icons: { declining: '↓', improving: '↑', stable: '→', mixed: '↕' },
    marker: 'arrow',
  },
  {
    key: 'confidence',
    label: 'Confidence',
    tones: { high: 'green', medium: 'amber', low: 'red' },
    labels: { high: 'High', medium: 'Medium', low: 'Low' },
    marker: 'text',
  },
];

/**
 * Display contract of one stat box: tone class suffix, marker, glyph and the word or
 * em dash to print. `tooltip` states the verbatim enum value, or that there is no data.
 * @typedef {Object} AnalysisIndicatorView
 * @property {'health' | 'risk_level' | 'trend' | 'confidence'} key
 * @property {string} label
 * @property {IndicatorTone} tone
 * @property {IndicatorMarker | null} marker
 * @property {string} icon
 * @property {string} text
 * @property {string} tooltip
 */

/**
 * The output-check warnings of the entity carrying the verdict, as the panel lists them above the
 * summary points. Only well-formed `{ code, path, message }` entries with a non-blank message are
 * kept, so a legacy run (which never carried the field) and a malformed value both yield an empty
 * list and the panel shows no warning block.
 * @param {{ warnings?: unknown } | null | undefined} source
 * @returns {Array<{ code: string, path: string, message: string }>}
 */
export function analysisWarnings(source) {
  const warnings = source?.warnings;
  if (!Array.isArray(warnings)) return [];
  return warnings.filter(
    (warning) =>
      warning &&
      typeof warning === 'object' &&
      typeof warning.code === 'string' &&
      typeof warning.path === 'string' &&
      typeof warning.message === 'string' &&
      warning.message.trim() !== '',
  );
}

/**
 * Every output-check warning a run detail carries, in the order the panel lists them: the
 * assessment child's first, then the other analyzer children's (the CR analyzer has its own
 * checks), each as `analysisWarnings` filters them. A legacy flat run is its own only result, so
 * its own field is read; a launch whose children are not loaded yields an empty list.
 * @param {{ analyses?: unknown, warnings?: unknown } | null | undefined} run
 * @returns {Array<{ code: string, path: string, message: string }>}
 */
export function analysisRunWarnings(run) {
  if (!run) return [];
  const children = Array.isArray(run.analyses) ? run.analyses : [];
  if (!children.length) return analysisWarnings(run);
  const assessment = children.filter((child) => child?.kind === 'assessment');
  const others = children.filter((child) => child?.kind !== 'assessment');
  return [...assessment, ...others].flatMap(analysisWarnings);
}

/**
 * Map the four assessment indicators to their stat-box views. `source` is the entity
 * carrying the verdict (the assessment child, or a legacy run that is its own result);
 * a missing source or a null value yields the em dash empty state.
 * @param {{ health?: string | null, risk_level?: string | null, trend?: string | null, confidence?: string | null } | null | undefined} source
 * @returns {AnalysisIndicatorView[]}
 */
export function analysisIndicatorViews(source) {
  return ANALYSIS_INDICATORS.map((indicator) => {
    const value = source?.[indicator.key];
    // The tone map is the enum guard: a value it does not own (including the prototype
    // key 'constructor') is outside the backend vocabulary and falls to the empty state.
    const known = value != null && Object.hasOwn(indicator.tones, value);
    return {
      key: indicator.key,
      label: indicator.label,
      tone: known ? indicator.tones[value] : 'gray',
      marker: known ? indicator.marker : null,
      icon: known ? (indicator.icons?.[value] ?? '') : '',
      text: known ? indicator.labels[value] : '—',
      tooltip: `${indicator.label}: ${value ?? 'no data'}${indicatorProbabilityNote(source, indicator.key, value)}`,
    };
  });
}

/**
 * ` (64%)` when the source is a Jev reading that carries a probability for the shown value,
 * otherwise an empty string. The model's verdict has no probabilities, so its tooltips stay as
 * they are.
 * @param {{ probabilities?: Record<string, Record<string, number>> } | null | undefined} source
 * @param {string} key
 * @param {string | null | undefined} value
 * @returns {string}
 */
function indicatorProbabilityNote(source, key, value) {
  const probability = value == null ? undefined : source?.probabilities?.[key]?.[value];
  return typeof probability === 'number' ? ` (${Math.round(probability * 100)}%)` : '';
}

/**
 * Jev's indicators as the API publishes them on the assessment child.
 * @typedef {Object} JevIndicators
 * @property {string | null} health
 * @property {string | null} risk_level
 * @property {string | null} trend
 * @property {string | null} confidence
 * @property {Record<string, Record<string, number>>} probabilities Probability per option, per indicator.
 */

/**
 * The `indicators_jev` payload of an assessment child, or null when the run stored none or the
 * value is not the documented object. Picks are read verbatim like the columns; `analysisIndicatorViews`
 * is the enum guard, so an unknown pick renders the em dash rather than being defaulted here.
 * @param {unknown} value
 * @returns {JevIndicators | null}
 */
export function analysisJevIndicators(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const probabilities =
    value.probabilities && typeof value.probabilities === 'object' && !Array.isArray(value.probabilities)
      ? value.probabilities
      : {};
  return {
    health: value.health ?? null,
    risk_level: value.risk_level ?? null,
    trend: value.trend ?? null,
    confidence: value.confidence ?? null,
    probabilities,
  };
}

/**
 * Entity the stat boxes must read. The launch carries no verdict of its own, so a launch
 * with a loaded assessment reads the child; a legacy pre-kind row or a launch whose
 * children are not loaded yet keeps reading its own flat fields.
 * @param {{ assessment?: AiAnalysisAssessment | null } | null | undefined} run
 * @returns {AiAnalysisAssessment | Record<string, unknown> | null}
 */
export function analysisVerdictSource(run) {
  if (!run) return null;
  return run.assessment ?? run;
}

/**
 * Display mapping of the summary_points categories (backend enum), shared by the CR
 * analyzer and the assessment. Colors reuse the AI badge palette (see AiBadge.vue)
 * so the list and the stat boxes read as one system.
 */
const SUMMARY_CATEGORIES = {
  positive: { icon: '↑', color: '#1e7e34' },
  risk: { icon: '↓', color: '#a32929' },
  warning: { icon: '⚠', color: '#8a5a00' },
  opportunity: { icon: '★', color: '#1e7e34' },
};

/**
 * Neutral view of a legacy plain-string summary point, the shape an assessment row
 * written before the categorized points still holds: no glyph at all, so the panel
 * renders no icon column, and #64748b - the panel's own muted text color
 * (--ds-color-text-muted fallback) - as the tone.
 */
const NEUTRAL_SUMMARY_VIEW = { icon: '', color: '#64748b' };

/**
 * Display contract of one summary point. `icon` is empty for a legacy plain-string
 * point, which the panel renders without the glyph column.
 * @typedef {Object} AnalysisSummaryPoint
 * @property {string} icon
 * @property {string} color
 * @property {string} text
 */

/**
 * Parse the summary_points JSON array string into display points. Two formats cross
 * the API: the CR analyzer and the current assessment send {category, text} objects,
 * while an assessment row written before the categorized shape holds plain strings
 * whose order alone carries their business importance and is therefore never
 * categorized. An all-string array is read as that legacy format and returned
 * verbatim, in order, with the neutral view; anything else is read as the
 * categorized format, where an unknown category or an empty text drops the point.
 * Absent, unparseable, non-array or junk-only input yields [] - rendering only, no
 * inference or synthesis of content.
 * @param {string | null | undefined} raw
 * @returns {AnalysisSummaryPoint[]}
 */
export function analysisSummaryPoints(raw) {
  if (!raw) return [];
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  // Every element a string: a pre-categorized assessment row. Empty strings drop,
  // the rest keep their order and their text untouched.
  if (parsed.every((point) => typeof point === 'string')) {
    return parsed.filter((text) => text).map((text) => ({ ...NEUTRAL_SUMMARY_VIEW, text }));
  }
  return parsed
    .filter(
      (point) =>
        point &&
        Object.hasOwn(SUMMARY_CATEGORIES, point.category) &&
        typeof point.text === 'string' &&
        point.text,
    )
    .map((point) => ({ ...SUMMARY_CATEGORIES[point.category], text: point.text }));
}

/**
 * Recommendation JSON DTO. Business descriptions copied from the backend AiRecommendation model.
 * Date attributes cross the API as UTC strings.
 * @typedef {Object} AiRecommendationDto
 * @property {number} id Unique identifier of this recommendation.
 * @property {number} run_id ID of the analysis run that produced this recommendation.
 * @property {number} offer_id ID of the offer this recommendation belongs to.
 * @property {string} title Severity-and-direction headline, e.g. "Sharp CR drop"; the adjective comes from the backend severity bucket. No numbers, entity names or periods.
 * @property {string} observation What the analysis observed in the offer data.
 * @property {string | null} [observation_summary] One sentence (max 120 chars) stating where and who, not by how much: the affected entity by label against the rest of the offer's traffic, e.g. "Acme Media against the rest of the offer's traffic, last 3 days". Numbers, percentages, metric enumerations and the internal benchmark are forbidden; the figures stay in observation.
 * @property {string} recommendation Suggested action for the offer manager.
 * @property {Record<string, unknown>} meta Structured details of the finding, such as affected segments; no inferred values.
 * @property {string} created_at Timestamp in UTC when the recommendation was stored.
 * @property {string | null} [priority] Planned display priority; assignment is backend follow-up work. Never inferred from content.
 * @property {'new' | 'reviewed' | null} [status] Planned shared review state; persistence is backend follow-up work. Never inferred from access.
 * @property {'negative' | 'positive' | 'opportunity' | 'neutral'} impact What the cited evidence is currently doing to the offer: negative (hurting performance now), positive (helping performance now), opportunity (upside that is not currently a problem) or neutral (worth watching, no clear effect yet). Answers "is this good or bad?", never how much it matters. Always present.
 * @property {'fix' | 'scale' | 'optimize' | 'investigate' | 'monitor'} action_type Kind of response the recommendation asks for; independent of impact, so a negative finding can be either fix or investigate. Always present.
 * @property {'cr'} source Analysis skill that produced the recommendation. CR segments is the only source today, so the UI does not display it yet. Always present.
 */

/**
 * Recommendation domain entity built from the API DTO.
 * Field descriptions duplicate the AiRecommendationDto typedef above; keep both in sync.
 */
export class AiRecommendation {
  /** @param {AiRecommendationDto} data */
  constructor(data) {
    /** @type {number} Unique identifier of this recommendation. */
    this.id = data.id;
    /** @type {number} ID of the analysis run that produced this recommendation. */
    this.run_id = data.run_id;
    /** @type {number} ID of the offer this recommendation belongs to. */
    this.offer_id = data.offer_id;
    /** @type {string} Severity-and-direction headline, e.g. "Sharp CR drop"; the adjective comes from the backend severity bucket. No numbers, entity names or periods. */
    this.title = data.title;
    /** @type {string} What the analysis observed in the offer data. */
    this.observation = data.observation;
    /** @type {string | null} One sentence (max 120 chars) stating where and who, not by how much: the affected entity by label against the rest of the offer's traffic, e.g. "Acme Media against the rest of the offer's traffic, last 3 days". Numbers, percentages, metric enumerations and the internal benchmark are forbidden; the figures stay in observation. */
    this.observation_summary = data.observation_summary ?? null;
    /** @type {string} Suggested action for the offer manager. */
    this.recommendation = data.recommendation;
    /** @type {Record<string, unknown>} Structured details of the finding, such as affected segments; no inferred values. */
    this.meta = data.meta;
    /** @type {string} Timestamp in UTC when the recommendation was stored. */
    this.created_at = data.created_at;
    /** @type {string | null} Planned display priority; assignment is backend follow-up work. Never inferred from content. */
    this.priority = data.priority ?? null;
    /** @type {'new' | 'reviewed' | null} Planned shared review state; persistence is backend follow-up work. Never inferred from access. */
    this.status = data.status ?? null;
    /** @type {'negative' | 'positive' | 'opportunity' | 'neutral'} What the cited evidence is currently doing to the offer: negative (hurting performance now), positive (helping performance now), opportunity (upside that is not currently a problem) or neutral (worth watching, no clear effect yet). Answers "is this good or bad?", never how much it matters. Always present. */
    this.impact = data.impact;
    /** @type {'fix' | 'scale' | 'optimize' | 'investigate' | 'monitor'} Kind of response the recommendation asks for; independent of impact, so a negative finding can be either fix or investigate. Always present. */
    this.action_type = data.action_type;
    /** @type {'cr'} Analysis skill that produced the recommendation. CR segments is the only source today, so the UI does not display it yet. Always present. */
    this.source = data.source;
  }
}

/**
 * Circle tone of an action element; gray is the neutral fallback for unknown or missing action types.
 * @typedef {'red' | 'green' | 'blue' | 'orange' | 'slate' | 'gray'} ActionTone
 */

/**
 * Semantic icon key of an action element; the components resolve it to a glyph class through actionIconClass().
 * @typedef {'arrow-down' | 'arrow-up' | 'chart' | 'warning' | 'eye' | 'dot'} ActionIconKey
 */

/**
 * Action element display contract: circle tone, semantic icon key and word for the
 * left column of the recommendation lists. action_type owns these colors, so the
 * owner-approved mapping below is a frozen contract: any change needs a new decision.
 * iconKey is semantic ('arrow-up' | 'arrow-down' | 'chart' | 'warning' | 'eye' | 'dot');
 * the components resolve it to a Line Awesome class through actionIconClass().
 */
export const ACTION_VIEWS = {
  fix: { tone: 'red', iconKey: 'arrow-down', label: 'Fix' },
  scale: { tone: 'green', iconKey: 'arrow-up', label: 'Scale' },
  optimize: { tone: 'blue', iconKey: 'chart', label: 'Optimize' },
  investigate: { tone: 'orange', iconKey: 'warning', label: 'Investigate' },
  monitor: { tone: 'slate', iconKey: 'eye', label: 'Monitor' },
};

/**
 * Unknown or missing action types: gray tone, neutral dot glyph, verbatim word.
 * @type {{ tone: ActionTone, iconKey: ActionIconKey }}
 */
const NEUTRAL_ACTION_VIEW = { tone: 'gray', iconKey: 'dot' };

/**
 * Own-property lookup keeps prototype keys ("constructor", "toString") out of the
 * mapping, so any unexpected value reaches the neutral fallback instead of breaking.
 * @param {string | null | undefined} actionType
 * @returns {{ tone: ActionTone, iconKey: ActionIconKey, label: string }}
 */
export function actionView(actionType) {
  if (Object.hasOwn(ACTION_VIEWS, actionType)) return ACTION_VIEWS[actionType];
  return { ...NEUTRAL_ACTION_VIEW, label: actionTypeLabel(actionType) };
}

/**
 * @param {string | null | undefined} actionType
 * @returns {ActionTone}
 */
export function actionTone(actionType) {
  return actionView(actionType).tone;
}

/**
 * @param {string | null | undefined} actionType
 * @returns {ActionIconKey}
 */
export function actionIconKey(actionType) {
  return actionView(actionType).iconKey;
}

/**
 * Line Awesome 1.3.0 glyph for each semantic iconKey; the library loads globally from
 * the CDN in index.html, so only class names belong here.
 */
const ACTION_ICON_CLASSES = {
  'arrow-down': 'la-arrow-down',
  'arrow-up': 'la-arrow-up',
  chart: 'la-chart-line',
  warning: 'la-exclamation-triangle',
  eye: 'la-eye',
  dot: 'la-circle',
};

/**
 * Single source of the action circle glyph: "la la-<name>" for a known action_type,
 * the neutral circle class for an unknown or missing one.
 * @param {string | null | undefined} actionType
 * @returns {string}
 */
export function actionIconClass(actionType) {
  const iconKey = actionIconKey(actionType);
  return `la ${ACTION_ICON_CLASSES[iconKey] ?? ACTION_ICON_CLASSES.dot}`;
}

const IMPACT_LABELS = {
  negative: 'Negative',
  positive: 'Positive',
  opportunity: 'Opportunity',
  neutral: 'Neutral',
};

/**
 * Verbatim impact word capitalized; unknown or missing stays neutral, never blank.
 * @param {string | null | undefined} impact
 * @returns {string}
 */
export function impactLabel(impact) {
  return Object.hasOwn(IMPACT_LABELS, impact) ? IMPACT_LABELS[impact] : IMPACT_LABELS.neutral;
}

/**
 * Known action types use the mapping word; unknown ones stay verbatim, null stays empty.
 * @param {string | null | undefined} actionType
 * @returns {string}
 */
export function actionTypeLabel(actionType) {
  if (actionType == null) return '';
  if (Object.hasOwn(ACTION_VIEWS, actionType)) return ACTION_VIEWS[actionType].label;
  const text = String(actionType);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Sub-label under the impact circle: "<Impact> <Action>", e.g. "Negative Fix".
 * @param {string | null | undefined} impact
 * @param {string | null | undefined} actionType
 * @returns {string}
 */
export function impactText(impact, actionType) {
  const action = actionTypeLabel(actionType);
  return action ? `${impactLabel(impact)} ${action}` : impactLabel(impact);
}
